import { useEffect, useState } from "react";
import type { DeliveryChannel, OrderDetail, OrderItem } from "@probetalab/contracts";
import { run } from "../lib/api.js";
import { money } from "../lib/format.js";
import { conditionLocked, previewRemoval, REMOVAL_REASONS, removalBlock } from "../lib/orderEdit.js";
import { useBranch } from "../lib/session.js";
import { useToast } from "../lib/toast.js";
import { errorMessage } from "../lib/useQuery.js";
import { ConditionPicker } from "./PatientPicker.js";
import { DoctorPicker, type DoctorChoice } from "./DoctorPicker.js";
import { Alert, Button, Field, Input, Pill, Segmented, Sheet, Textarea } from "./ui.js";

/**
 * Editing an order after registration (Very, 2026-11-02): remove a study added by mistake, and
 * correct the visit data. Both sheets explain a refusal instead of offering a button that fails.
 */

type ReasonChoice = (typeof REMOVAL_REASONS)[number] | "other";

export function RemoveItemSheet({ order, item, onClose, onDone }: { order: OrderDetail; item: OrderItem | null; onClose: () => void; onDone: (o: OrderDetail) => void }) {
  const branch = useBranch();
  const toast = useToast();
  const [choice, setChoice] = useState<ReasonChoice>(REMOVAL_REASONS[0]);
  const [other, setOther] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setChoice(REMOVAL_REASONS[0]);
    setOther("");
  }, [item?.id]);

  const block = item ? removalBlock(order, item) : null;
  const preview = item && !block ? previewRemoval(order, item) : null;
  const reason = choice === "other" ? other.trim() : choice;

  async function remove() {
    if (!item) return;
    setBusy(true);
    try {
      const d = await run<"order_item.cancel", OrderDetail>("order_item.cancel", { order_item_id: item.id, reason }, { branch_id: branch.id });
      toast.success(`${item.study_clave} se quitó de la solicitud.`);
      onDone(d);
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet
      open={item !== null}
      onClose={onClose}
      title={item ? `Quitar ${item.study_clave} · ${item.study_name}` : ""}
      footer={
        block ? (
          <Button variant="primary" onClick={onClose}>
            Entendido
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              Conservar
            </Button>
            <Button variant="danger" icon="x" busy={busy} disabled={reason.length < 3} onClick={() => void remove()}>
              Quitar estudio
            </Button>
          </>
        )
      }
    >
      {block ? (
        <Alert tone="amber">{block}</Alert>
      ) : (
        item &&
        preview && (
          <div className="stack-lg">
            <Field label="Motivo">
              <div className="pills">
                {REMOVAL_REASONS.map((r) => (
                  <Pill key={r} on={choice === r} onClick={() => setChoice(r)}>
                    {r}
                  </Pill>
                ))}
                <Pill on={choice === "other"} onClick={() => setChoice("other")}>
                  Otro
                </Pill>
              </div>
            </Field>
            {choice === "other" && (
              <Field label="¿Por qué se quita?">
                <Input value={other} onChange={(e) => setOther(e.target.value)} placeholder="Escribe el motivo" autoFocus />
              </Field>
            )}
            <div className="panel stack" style={{ gap: 6 }}>
              <div className="row between">
                <span>{item.package_id ? "Su parte del precio del paquete" : "Precio del estudio"}</span>
                <span className="money">− {money(item.price_cents)}</span>
              </div>
              <div className="row between" style={{ fontWeight: 600 }}>
                <span>El total queda en</span>
                <span className="money">{money(preview.total_cents)}</span>
              </div>
              {preview.balance_cents > 0 && (
                <div className="row between small muted">
                  <span>Adeudo</span>
                  <span className="money">{money(preview.balance_cents)}</span>
                </div>
              )}
            </div>
            {preview.credit_cents > 0 && (
              <Alert tone="blue">
                Queda un saldo a favor de <strong>{money(preview.credit_cents)}</strong>. Los pagos registrados no cambian.
              </Alert>
            )}
            {item.package_id && <p className="small muted">Este estudio es parte de un paquete; los demás estudios del paquete se quedan en la solicitud.</p>}
            {preview.tube_released && (
              <p className="small muted">
                El tubo <span className="mono">{preview.tube_released}</span> ya no tiene otros estudios: deja de estar pendiente.
              </p>
            )}
          </div>
        )
      )}
    </Sheet>
  );
}

type Fasting = "unknown" | "yes" | "no";

interface HeaderDraft {
  doctor: DoctorChoice;
  channels: DeliveryChannel[];
  fasting: Fasting;
  fastingHours: string;
  condition: string;
  notes: string;
}

function draftFrom(o: OrderDetail): HeaderDraft {
  return {
    doctor: { doctor_id: o.doctor_id, doctor_text: o.doctor_text, display: o.doctor_display },
    channels: o.delivery_channels,
    fasting: o.fasting_hours === null ? "unknown" : o.fasting_hours === 0 ? "no" : "yes",
    fastingHours: o.fasting_hours ? String(o.fasting_hours) : "",
    condition: o.condition ?? "",
    notes: o.clinical_notes ?? "",
  };
}

/** The visit data as captured in Recepción, editable while the order is open. */
export function EditHeaderSheet({ order, open, onClose, onDone }: { order: OrderDetail; open: boolean; onClose: () => void; onDone: (o: OrderDetail) => void }) {
  const branch = useBranch();
  const toast = useToast();
  const [d, setD] = useState<HeaderDraft>(() => draftFrom(order));
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setD(draftFrom(order));
    // Reset only when the sheet opens; the order prop refreshes behind it.
  }, [open]);
  const locked = conditionLocked(order);
  const set = (patch: Partial<HeaderDraft>) => setD((x) => ({ ...x, ...patch }));
  const toggleChannel = (c: DeliveryChannel) => set({ channels: d.channels.includes(c) ? d.channels.filter((x) => x !== c) : [...d.channels, c] });
  const hours = d.fastingHours === "" ? null : Math.max(0, Math.min(48, Math.round(Number(d.fastingHours) || 0)));

  async function save() {
    setBusy(true);
    try {
      const next = await run<"order.update_header", OrderDetail>(
        "order.update_header",
        {
          order_id: order.id,
          doctor_id: d.doctor.doctor_id,
          doctor_text: d.doctor.doctor_text,
          delivery_channels: d.channels,
          clinical_notes: d.notes.trim() || null,
          fasting_hours: d.fasting === "no" ? 0 : d.fasting === "unknown" ? null : hours,
          // A locked condition is sent as it is, so the server sees no change to it.
          ...(locked ? {} : { condition: d.condition.trim() || null }),
        },
        { branch_id: branch.id },
      );
      toast.success("Datos de la visita actualizados.");
      onDone(next);
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={`Editar datos de la visita · ${order.folio}`}
      wide
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" icon="check" busy={busy} disabled={d.channels.length === 0} onClick={() => void save()}>
            Guardar cambios
          </Button>
        </>
      }
    >
      <div className="stack-lg">
        <Field label="Médico solicitante">
          <DoctorPicker value={d.doctor} onChange={(doctor) => set({ doctor })} />
        </Field>
        <div className="grid-2">
          <Field label="Entrega de resultados" error={d.channels.length === 0 ? "Elige al menos una forma de entrega." : null}>
            <div className="pills">
              <Pill on={d.channels.includes("whatsapp")} icon="whatsapp" onClick={() => toggleChannel("whatsapp")}>
                WhatsApp
              </Pill>
              <Pill on={d.channels.includes("print")} icon="print" onClick={() => toggleChannel("print")}>
                Impreso
              </Pill>
            </div>
          </Field>
          <Field label="Ayuno">
            <div className="row wrap">
              <Segmented value={d.fasting} onChange={(fasting) => set({ fasting })} options={[{ value: "unknown", label: "No aplica" }, { value: "yes", label: "Sí" }, { value: "no", label: "No" }]} />
              {d.fasting === "yes" && (
                <div className="input-wrap" style={{ width: 150 }}>
                  <span className="suffix">horas</span>
                  <Input inputMode="numeric" value={d.fastingHours} onChange={(e) => set({ fastingHours: e.target.value })} placeholder="8–12" aria-label="Horas de ayuno" />
                </div>
              )}
            </div>
          </Field>
        </div>
        <div className="grid-2">
          <Field label="Condición especial" hint={locked ? undefined : "Selecciona los valores de referencia especiales (embarazo, por ejemplo)."}>
            {locked ? (
              <Alert tone="amber">
                <strong>{order.condition ?? "Ninguna"}.</strong> Ya no se puede cambiar: hay resultados capturados con los valores de referencia de esta condición.
              </Alert>
            ) : (
              <ConditionPicker value={d.condition} onChange={(condition) => set({ condition })} />
            )}
          </Field>
          <Field label="Notas clínicas" optional hint="Motivo del estudio, medicamentos o suplementos, semanas de gestación, fecha de última menstruación.">
            <Textarea value={d.notes} onChange={(e) => set({ notes: e.target.value })} rows={3} />
          </Field>
        </div>
      </div>
    </Sheet>
  );
}
